import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { MoreActions } from "@/components/ui/MoreActions";
import { TextArea } from "@/components/ui/Textarea";
import { TableRowContextMenu } from "@/components/table-row-context-menu";
import { TagsVariationEditor } from "@/components/tool-variation-tags-editor";
import { useCommandPalette } from "@/contexts/CommandPalette";
import { useLatestDeployment } from "@/hooks/toolTypes";
import { ToolUpdateFields } from "@/hooks/useToolUpdate";
import { TOOL_NAME_REGEX } from "@/lib/constants";
import { Tool, Toolset, isHttpTool } from "@/lib/toolTypes";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/Label";
import { Switch } from "@/components/ui/Switch";
import { Icon } from "@/components/ui/Icon";
import { Stack } from "@/components/ui/Stack";
import {
  ChevronDown,
  FileCode,
  Layers,
  PencilRuler,
  SquareFunction,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AnnotationBadges } from "./AnnotationBadges";
import { ToolVariationBadge } from "../tool-variation-badge";
import { McpIcon } from "@/components/ui/McpIcon";
import { SimpleTooltip } from "../ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { MethodBadge } from "./MethodBadge";
import { SubtoolsBadge } from "./SubtoolsBadge";

interface ToolListProps {
  tools: Tool[]; // Accepts all tool types, filters to Tool internally
  toolset?: Toolset; // Optionally specificy the toolset to provide rows with additional context
  onToolUpdate?: (
    tool: Tool,
    updates: ToolUpdateFields,
  ) => void | Promise<void>;
  /** True while a tool update is in flight; disables Save in the edit dialog. */
  isToolUpdating?: boolean;
  onToolsRemove?: (toolUrns: string[]) => void;
  onAddToToolset?: (toolUrns: string[]) => void;
  onCreateToolset?: (toolUrns: string[]) => void;
  onTestInPlayground?: () => void;
  className?: string;
  /** When true, hides action menus and selection checkboxes for a view-only presentation. */
  readOnly?: boolean;
  // Selection mode props for AddToolsDialog
  selectionMode?: "add" | "remove";
  selectedUrns?: string[];
  onSelectionChange?: (urns: string[]) => void;
  onToolClick?: (tool: Tool) => void;
}

interface ToolGroup {
  type: "package" | "function" | "custom" | "higher_order" | "external-mcp";
  icon:
    | "file-code"
    | "square-function"
    | "square-stack"
    | "pencil-ruler"
    | "mcp";
  title: string;
  tools: Tool[];
  packageName?: string;
}

const HTTP_METHOD_ORDER = ["GET", "POST", "PUT", "DELETE"];

function sortToolsByMethod(tools: Tool[]): Tool[] {
  const httpTools = tools.filter(isHttpTool);
  const nonHttpTools = tools.filter((t) => !isHttpTool(t));

  // Group tools by method
  const toolsByMethod = new Map<string, typeof httpTools>();
  httpTools.forEach((tool) => {
    const method = tool.httpMethod?.toUpperCase() || "UNKNOWN";
    if (!toolsByMethod.has(method)) {
      toolsByMethod.set(method, []);
    }
    toolsByMethod.get(method)!.push(tool);
  });

  const sortedTools: Tool[] = [];

  // Round-robin through GET, POST, PUT, DELETE continuously
  // When PUT runs out, use PATCH instead (both are orange/warning)
  while (httpTools.length > sortedTools.length) {
    HTTP_METHOD_ORDER.forEach((method) => {
      let toolsForMethod = toolsByMethod.get(method);

      // If PUT is empty but PATCH exists, use PATCH instead (both are orange)
      if (
        method === "PUT" &&
        (!toolsForMethod || toolsForMethod.length === 0)
      ) {
        toolsForMethod = toolsByMethod.get("PATCH");
      }

      if (toolsForMethod && toolsForMethod.length > 0) {
        sortedTools.push(toolsForMethod.shift()!);
      }
    });

    // Also handle any other methods not in our standard order
    toolsByMethod.forEach((tools, method) => {
      if (
        !HTTP_METHOD_ORDER.includes(method) &&
        method !== "PATCH" &&
        tools.length > 0
      ) {
        sortedTools.push(tools.shift()!);
      }
    });
  }

  return [...sortedTools, ...nonHttpTools];
}

function groupTools(
  tools: Tool[],
  documentIdToName?: Record<string, string>,
  functionIdToName?: Record<string, string>,
): ToolGroup[] {
  const groups: ToolGroup[] = [];
  const packageMap = new Map<string, Tool[]>();
  const functionMap = new Map<string, Tool[]>();
  const registryServerNameToTools = new Map<string, Tool[]>();
  const functionTools: Tool[] = [];
  const customTools: Tool[] = [];
  const higherOrderTools: Tool[] = [];

  tools.forEach((tool) => {
    if (tool.type === "http") {
      let groupKey: string | undefined;

      if (tool.packageName) {
        groupKey = tool.packageName;
      } else if (tool.openapiv3DocumentId && documentIdToName) {
        groupKey = documentIdToName[tool.openapiv3DocumentId];
      } else if (tool.deploymentId) {
        groupKey = tool.deploymentId;
      }

      if (groupKey) {
        const existing = packageMap.get(groupKey) || [];
        packageMap.set(groupKey, [...existing, tool]);
      } else {
        // HTTP tools without any identifier go to custom
        customTools.push(tool);
      }
    } else if (tool.type === "function") {
      let groupKey: string | undefined;

      if (tool.functionId && functionIdToName) {
        groupKey = functionIdToName[tool.functionId];
      }

      if (groupKey) {
        const existing = functionMap.get(groupKey) || [];
        functionMap.set(groupKey, [...existing, tool]);
      } else {
        // Function tools without a source go to the generic functions group
        functionTools.push(tool);
      }
    } else if (tool.type === "external-mcp") {
      const groupKey = tool.registryServerName;
      const existing = registryServerNameToTools.get(groupKey) || [];
      registryServerNameToTools.set(groupKey, [...existing, tool]);
    } else {
      // Everything else (prompts without higher order, etc.)
      customTools.push(tool);
    }
  });

  // Add package groups with sorted tools
  packageMap.forEach((tools, packageName) => {
    groups.push({
      type: "package",
      icon: "file-code",
      title: packageName,
      tools: sortToolsByMethod(tools),
      packageName,
    });
  });

  // Add function groups
  functionMap.forEach((tools, functionName) => {
    groups.push({
      type: "function",
      icon: "square-function",
      title: functionName,
      tools,
      packageName: functionName,
    });
  });

  // Add function tools group
  if (functionTools.length > 0) {
    groups.push({
      type: "function",
      icon: "square-function",
      title: "Functions",
      tools: functionTools,
    });
  }

  // Add external MCP tools group
  registryServerNameToTools.forEach((tools, registryServerName) => {
    groups.push({
      type: "external-mcp",
      icon: "mcp",
      title: registryServerName,
      tools: sortToolsByMethod(tools),
    });
  });

  // Add custom tools group with sorted tools
  if (customTools.length > 0) {
    groups.push({
      type: "custom",
      icon: "pencil-ruler",
      title: "Custom",
      tools: sortToolsByMethod(customTools),
    });
  }

  // Add higher order tools group
  if (higherOrderTools.length > 0) {
    groups.push({
      type: "higher_order",
      icon: "square-stack",
      title: "Higher Order Tools",
      tools: higherOrderTools,
    });
  }

  return groups;
}

function getIcon(icon: ToolGroup["icon"]) {
  switch (icon) {
    case "file-code":
      return FileCode;
    case "square-function":
      return SquareFunction;
    case "square-stack":
      return Layers;
    case "pencil-ruler":
      return PencilRuler;
    case "mcp":
      return McpIcon;
  }
}

function ToolRow({
  tool,
  availableToolUrns, // Context for the subtools badge
  groupName,
  onUpdate,
  isUpdating,
  isSelected,
  isFocused,
  onCheckboxChange,
  onTestInPlayground,
  onRemove,
  onToolClick,
  readOnly,
}: {
  tool: Tool;
  availableToolUrns?: string[];
  groupName: string;
  onUpdate?: (updates: ToolUpdateFields) => void | Promise<void>;
  isUpdating?: boolean;
  isSelected: boolean;
  isFocused: boolean;
  onCheckboxChange: (checked: boolean) => void;
  onTestInPlayground?: () => void;
  onRemove?: () => void;
  onToolClick?: (tool: Tool) => void;
  readOnly?: boolean;
}) {
  const isDisabled = false;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editType, setEditType] = useState<
    "name" | "description" | "annotations" | "tags"
  >("name");
  const [editValue, setEditValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Annotation editing state
  const [annotTitle, setAnnotTitle] = useState("");
  const [annotReadOnly, setAnnotReadOnly] = useState(false);
  const [annotDestructive, setAnnotDestructive] = useState(false);
  const [annotIdempotent, setAnnotIdempotent] = useState(false);
  const [annotOpenWorld, setAnnotOpenWorld] = useState(false);

  // Tags editing state (HTTP and function tools)
  const [tagsValue, setTagsValue] = useState<string[] | undefined>(undefined);

  const hasAnnotations = tool.type === "http" || tool.type === "function";
  // TODO: extend tag variations to prompt tools once they support tags.
  const supportsTags = tool.type === "http" || tool.type === "function";
  // Memoize: the inline `[]` fallback would produce a fresh reference per
  // render and invalidate downstream memoization in TagsVariationEditor.
  const baseTags = useMemo(
    () => (tool.type === "http" || tool.type === "function" ? tool.tags : []),
    [tool],
  );
  const origTags = supportsTags ? tool.variation?.tags : undefined;

  const openEditDialog = (
    type: "name" | "description" | "annotations" | "tags",
  ) => {
    setEditType(type);
    if (type === "annotations") {
      setAnnotTitle(tool.variation?.title ?? tool.annotations?.title ?? "");
      setAnnotReadOnly(
        tool.variation?.readOnlyHint ?? tool.annotations?.readOnlyHint ?? false,
      );
      setAnnotDestructive(
        tool.variation?.destructiveHint ??
          tool.annotations?.destructiveHint ??
          false,
      );
      setAnnotIdempotent(
        tool.variation?.idempotentHint ??
          tool.annotations?.idempotentHint ??
          false,
      );
      setAnnotOpenWorld(
        tool.variation?.openWorldHint ??
          tool.annotations?.openWorldHint ??
          false,
      );
    } else if (type === "tags") {
      setTagsValue(origTags);
    } else {
      setEditValue(type === "name" ? tool.name : tool.description);
    }
    setError(null);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (editType === "name" && !TOOL_NAME_REGEX.test(editValue)) {
      setError("Tool name may only contain letters, numbers, and underscores");
      return;
    }

    let updates: ToolUpdateFields;
    if (editType === "annotations") {
      updates = {
        title: annotTitle || undefined,
        readOnlyHint: annotReadOnly,
        destructiveHint: annotDestructive,
        idempotentHint: annotIdempotent,
        openWorldHint: annotOpenWorld,
      };
    } else if (editType === "tags") {
      // tags key must always be present so the upsert form spread correctly
      // overwrites any prior variation tags (sending undefined drops the key
      // from the wire body, signalling no override).
      updates = { tags: tagsValue };
    } else {
      updates = { [editType]: editValue };
    }

    try {
      await onUpdate?.(updates);
      setEditDialogOpen(false);
    } catch (err) {
      // Toast is surfaced by useToolUpdate's onError; keep inline message for
      // dialog visibility.
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  };

  const handleCopyName = async () => {
    await navigator.clipboard.writeText(tool.name);
  };

  const actions = [
    {
      label: "Edit name",
      onClick: () => openEditDialog("name"),
      icon: "pencil" as const,
      disabled: isDisabled,
    },
    {
      label: "Edit description",
      onClick: () => openEditDialog("description"),
      icon: "pencil" as const,
    },
    ...(hasAnnotations
      ? [
          {
            label: "Edit annotations",
            onClick: () => openEditDialog("annotations"),
            icon: "pencil" as const,
          },
        ]
      : []),
    ...(supportsTags
      ? [
          {
            label: "Edit tags",
            onClick: () => openEditDialog("tags"),
            icon: "pencil" as const,
          },
        ]
      : []),
    {
      label: "Copy name",
      onClick: handleCopyName,
      icon: "copy" as const,
    },
    ...(onTestInPlayground
      ? [
          {
            label: "Test in Playground",
            onClick: onTestInPlayground,
            icon: "message-circle" as const,
          },
        ]
      : []),
    ...(onRemove
      ? [
          {
            label: "Remove",
            onClick: onRemove,
            icon: "trash" as const,
            destructive: true,
          },
        ]
      : []),
  ];

  const toolPrefix = tool.name.startsWith(groupName + "_")
    ? groupName + "_"
    : "";
  const toolNameNoPrefix = tool.name.replace(toolPrefix, "");

  return (
    <>
      <TableRowContextMenu actions={readOnly ? [] : actions}>
        <div
          className={cn(
            "group border-neutral-softest hover:bg-muted relative flex items-center justify-between overflow-hidden border-b py-4 pr-3 pl-4 transition-colors last:border-b-0",
            isFocused && "bg-muted",
            onToolClick && "cursor-pointer",
          )}
          onClick={() => onToolClick?.(tool)}
        >
          <div className="flex min-w-0 flex-[0_1_60%] items-center gap-4">
            {!readOnly && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  "shrink-0 transition-opacity",
                  !isSelected &&
                    !isFocused &&
                    "opacity-0 group-hover:opacity-100",
                )}
              />
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              <Stack direction="horizontal" gap={2} align="center">
                <p className="text-foreground truncate text-sm leading-6">
                  {toolPrefix && (
                    <Text small muted className="inline">
                      {toolPrefix}
                    </Text>
                  )}
                  {toolNameNoPrefix}
                </p>
                <ToolVariationBadge tool={tool} />
                <AnnotationBadges tool={tool} />
              </Stack>
              <p className="text-muted-foreground truncate text-sm leading-6">
                {tool.description || "No description"}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-4">
            {tool.type === "http" && tool.httpMethod && (
              <MethodBadge method={tool.httpMethod} />
            )}
            {tool.type === "prompt" && (
              <SubtoolsBadge
                tool={tool}
                availableToolUrns={availableToolUrns ?? []}
              />
            )}
            {!readOnly && <MoreActions actions={actions} />}
          </div>
        </div>
      </TableRowContextMenu>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>
              {editType === "annotations"
                ? "Edit annotations"
                : editType === "tags"
                  ? "Edit tags"
                  : editType === "name"
                    ? "Edit tool name"
                    : "Edit description"}
            </Dialog.Title>
            <Dialog.Description>
              {editType === "annotations"
                ? `Override behavior hints for '${tool.name}'`
                : editType === "tags"
                  ? `Override tags for '${tool.name}'`
                  : editType === "name"
                    ? `Update the name of tool '${tool.name}'`
                    : `Update the description of tool '${tool.name}'`}
            </Dialog.Description>
          </Dialog.Header>
          <div className="space-y-4 py-4">
            {editType === "annotations" ? (
              <Stack gap={4}>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Title</Label>
                  <Input
                    value={annotTitle}
                    onChange={setAnnotTitle}
                    placeholder="Display name override"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Behavior Hints</Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Read-only</p>
                        <p className="text-muted-foreground text-xs">
                          Tool does not modify its environment
                        </p>
                      </div>
                      <Switch
                        checked={annotReadOnly}
                        onCheckedChange={setAnnotReadOnly}
                        aria-label="Read-only hint"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Destructive</p>
                        <p className="text-muted-foreground text-xs">
                          Tool may perform destructive updates
                        </p>
                      </div>
                      <Switch
                        checked={annotDestructive}
                        onCheckedChange={setAnnotDestructive}
                        aria-label="Destructive hint"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Idempotent</p>
                        <p className="text-muted-foreground text-xs">
                          Repeated calls with same arguments have no additional
                          effect
                        </p>
                      </div>
                      <Switch
                        checked={annotIdempotent}
                        onCheckedChange={setAnnotIdempotent}
                        aria-label="Idempotent hint"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Open-world</p>
                        <p className="text-muted-foreground text-xs">
                          Tool interacts with external entities
                        </p>
                      </div>
                      <Switch
                        checked={annotOpenWorld}
                        onCheckedChange={setAnnotOpenWorld}
                        aria-label="Open-world hint"
                      />
                    </div>
                  </div>
                </div>
              </Stack>
            ) : editType === "tags" ? (
              <TagsVariationEditor
                baseTags={baseTags}
                value={tagsValue}
                onChange={setTagsValue}
              />
            ) : editType === "name" ? (
              <Stack gap={2}>
                <Input
                  value={editValue}
                  onChange={setEditValue}
                  placeholder="Tool name"
                />
                {tool.variation?.name &&
                  tool.variation?.name !== tool.canonical?.name && (
                    <Stack direction="horizontal" gap={2} align="center">
                      <Icon
                        name="layers-2"
                        size="small"
                        className="text-muted-foreground/70"
                      />
                      <Text small muted>
                        Original name:
                      </Text>
                      <Text small muted>
                        {tool.canonical?.name}
                      </Text>
                    </Stack>
                  )}
              </Stack>
            ) : (
              <Stack gap={2}>
                <TextArea
                  value={editValue}
                  onChange={setEditValue}
                  placeholder="Tool description"
                  rows={3}
                />
                {tool.variation?.description &&
                  tool.variation?.description !==
                    tool.canonical?.description && (
                    <Stack className="border-border/70 border p-2">
                      <Text small muted className="inline font-medium">
                        <Icon
                          name="layers-2"
                          size="small"
                          className="text-muted-foreground/70 inline align-text-bottom"
                        />{" "}
                        Original Description
                      </Text>
                      <Text small muted>
                        {tool.canonical?.description}
                      </Text>
                    </Stack>
                  )}
              </Stack>
            )}
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
          <Dialog.Footer>
            <Button
              variant="tertiary"
              onClick={() => setEditDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={isUpdating}>
              Save
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
}

function ToolGroupHeader({
  group,
  isExpanded,
  onToggle,
  isFirstGroup = false,
  allSelected,
  onSelectAll,
  readOnly,
}: {
  group: ToolGroup;
  isExpanded: boolean;
  onToggle: () => void;
  isFirstGroup?: boolean;
  allSelected: boolean;
  onSelectAll: () => void;
  readOnly?: boolean;
}) {
  const Icon = getIcon(group.icon);

  return (
    <div
      className={cn(
        "group/header bg-surface-secondary-default flex w-full items-center justify-between py-4 pr-3 pl-4",
        isExpanded && "border-neutral-softest border-b",
        !isFirstGroup && "border-neutral-softest border-t",
      )}
    >
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.title} group`}
        className="flex items-center gap-4 transition-opacity hover:opacity-70"
      >
        <div className="relative size-4 shrink-0">
          <Icon
            className={cn(
              "absolute inset-0 size-4 transition-opacity",
              !readOnly && "group-hover/header:opacity-0",
            )}
            strokeWidth={1.5}
          />
          {!readOnly && (
            <SimpleTooltip
              tooltip={`${allSelected ? "Deselect" : "Select"} ${group.tools.length} tools`}
            >
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className={cn(
                  "absolute inset-0 opacity-0 transition-opacity",
                  "group-hover/header:opacity-100",
                )}
              />
            </SimpleTooltip>
          )}
        </div>
        <p className="text-foreground text-sm leading-6">{group.title}</p>
      </button>
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.title} group`}
        className="transition-opacity hover:opacity-70"
      >
        <ChevronDown
          className={cn(
            "size-4 transition-transform",
            isExpanded ? "rotate-180" : "rotate-0",
          )}
          strokeWidth={1.5}
        />
      </button>
    </div>
  );
}

export function ToolList({
  tools,
  toolset,
  onToolUpdate,
  isToolUpdating,
  onToolsRemove,
  onAddToToolset,
  onCreateToolset,
  onTestInPlayground,
  className,
  readOnly,
  selectionMode,
  selectedUrns = [],
  onSelectionChange,
  onToolClick,
}: ToolListProps): JSX.Element {
  const { data: deployment } = useLatestDeployment();

  const documentIdToName = useMemo(() => {
    return deployment?.deployment?.openapiv3Assets?.reduce(
      (acc, asset) => {
        acc[asset.id] = asset.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [deployment]);

  const functionIdToName = useMemo(() => {
    return deployment?.deployment?.functionsAssets?.reduce(
      (acc, asset) => {
        acc[asset.id] = asset.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [deployment]);

  const groups = useMemo(
    () => groupTools(tools, documentIdToName, functionIdToName),
    [tools, documentIdToName, functionIdToName],
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  useEffect(() => {
    setExpandedGroups(new Set(groups.map((_, i) => i)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset expanded state when group count changes, not when tools within groups change
  }, [groups.length]);

  // For normal mode (remove tools from toolset)
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(
    new Set(),
  );

  // For selection mode (add tools to toolset) - use controlled state
  const selectedSet = useMemo(
    () => new Set(selectionMode === "add" ? selectedUrns : []),
    [selectionMode, selectedUrns],
  );

  // All tools are identified by toolUrn
  const getToolIdentifier = (tool: Tool): string => {
    return tool.toolUrn;
  };

  const [focusedToolIndex, setFocusedToolIndex] = useState<number>(-1);
  const { addActions, removeActions, setContextBadge } = useCommandPalette();

  const hasChanges =
    selectionMode === "add"
      ? selectedSet.size > 0
      : selectedForRemoval.size > 0;

  // Get flat list of all visible tools for keyboard navigation
  const visibleTools = useMemo(
    () =>
      groups.flatMap((group, groupIndex) =>
        expandedGroups.has(groupIndex) ? group.tools : [],
      ),
    [groups, expandedGroups],
  );

  // Create a map of tool ID -> index for O(1) lookups instead of O(n) findIndex
  const toolIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    visibleTools.forEach((tool, index) => {
      map.set(getToolIdentifier(tool), index);
    });
    return map;
  }, [visibleTools]);

  const handleCheckboxChange = useCallback(
    (toolId: string, checked: boolean) => {
      if (selectionMode === "add" && onSelectionChange) {
        // For selection mode, update parent state
        const next = new Set(selectedUrns);
        if (checked) {
          next.add(toolId);
        } else {
          next.delete(toolId);
        }
        onSelectionChange(Array.from(next));
      } else {
        // For normal mode, update local state
        setSelectedForRemoval((prev) => {
          const next = new Set(prev);
          if (checked) {
            next.add(toolId);
          } else {
            next.delete(toolId);
          }
          return next;
        });
      }
    },
    [selectionMode, onSelectionChange, selectedUrns],
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or the command palette is open
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        document.querySelector("[cmdk-root]")
      ) {
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        if (focusedToolIndex >= 0) {
          setFocusedToolIndex(-1);
          if (hasChanges) {
            setSelectedForRemoval(new Set());
          }
        } else if (hasChanges) {
          setSelectedForRemoval(new Set());
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedToolIndex((prev) =>
          prev < visibleTools.length - 1 ? prev + 1 : prev,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedToolIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === " " && focusedToolIndex >= 0) {
        e.preventDefault();
        const tool = visibleTools[focusedToolIndex];
        if (tool) {
          const toolId = getToolIdentifier(tool);
          const isCurrentlySelected =
            selectionMode === "add"
              ? selectedSet.has(toolId)
              : selectedForRemoval.has(toolId);
          handleCheckboxChange(toolId, !isCurrentlySelected);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    hasChanges,
    focusedToolIndex,
    visibleTools,
    selectionMode,
    selectedSet,
    selectedForRemoval,
    handleCheckboxChange,
  ]);

  // Register command palette actions and context badge when tools are selected
  // Skip this in selection mode - the dialog handles its own UI
  useEffect(() => {
    if (selectionMode === "add") {
      return;
    }

    const toolActionIds = ["add-to-toolset", "create-toolset", "remove-tools"];

    if (!hasChanges) {
      removeActions(toolActionIds);
      setContextBadge(null);
      return;
    }

    const count = selectedForRemoval.size;

    // Set the context badge
    setContextBadge(`${count} tool${count === 1 ? "" : "s"} selected`);

    const actions = [
      ...(onAddToToolset
        ? [
            {
              id: "add-to-toolset",
              label: "Add to MCP server",
              icon: "plus",
              group: "Tool Actions",
              onSelect: () => {
                setSelectedForRemoval((current) => {
                  onAddToToolset(Array.from(current));
                  return new Set();
                });
              },
            },
          ]
        : []),
      ...(onCreateToolset
        ? [
            {
              id: "create-toolset",
              label: "Create MCP server",
              icon: "copy",
              group: "Tool Actions",
              onSelect: () => {
                setSelectedForRemoval((current) => {
                  onCreateToolset(Array.from(current));
                  return new Set();
                });
              },
            },
          ]
        : []),
      {
        id: "remove-tools",
        label: "Remove",
        icon: "trash",
        group: "Tool Actions",
        onSelect: () => {
          setSelectedForRemoval((current) => {
            if (current.size > 0) {
              onToolsRemove?.(Array.from(current));
            }
            return new Set();
          });
        },
      },
    ];

    addActions(actions);

    // Clean up actions and badge when component unmounts or selection changes
    return () => {
      removeActions(toolActionIds);
      setContextBadge(null);
    };
  }, [
    selectionMode,
    hasChanges,
    selectedForRemoval.size,
    onAddToToolset,
    onCreateToolset,
    onToolsRemove,
    addActions,
    removeActions,
    setContextBadge,
  ]);

  const toggleGroup = (index: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleCancel = () => {
    setSelectedForRemoval(new Set());
  };

  const handleSelectAllInGroup = (group: ToolGroup) => {
    const groupToolIds = group.tools.map(getToolIdentifier);
    const currentSelection =
      selectionMode === "add" ? selectedSet : selectedForRemoval;
    const allSelected = groupToolIds.every((id) => currentSelection.has(id));

    if (selectionMode === "add" && onSelectionChange) {
      // For selection mode, update parent state
      const next = new Set(selectedUrns);
      if (allSelected) {
        // Deselect all in group
        groupToolIds.forEach((id) => {
          void next.delete(id);
        });
      } else {
        // Select all in group
        groupToolIds.forEach((id) => {
          void next.add(id);
        });
      }
      onSelectionChange(Array.from(next));
    } else {
      // For normal mode, update local state
      setSelectedForRemoval((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          // Deselect all in group
          groupToolIds.forEach((id) => {
            void next.delete(id);
          });
        } else {
          // Select all in group
          groupToolIds.forEach((id) => {
            void next.add(id);
          });
        }
        return next;
      });
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "border-neutral-softest w-full overflow-hidden border",
          className,
        )}
      >
        {groups.map((group, index) => {
          const groupToolIds = group.tools.map(getToolIdentifier);
          const currentSelection =
            selectionMode === "add" ? selectedSet : selectedForRemoval;
          const allSelected = groupToolIds.every((id) =>
            currentSelection.has(id),
          );

          return (
            <div
              key={`${group.type}-${group.title}-${index}`}
              className="w-full"
            >
              <ToolGroupHeader
                group={group}
                isExpanded={expandedGroups.has(index)}
                onToggle={() => toggleGroup(index)}
                isFirstGroup={index === 0}
                allSelected={allSelected}
                onSelectAll={() => handleSelectAllInGroup(group)}
                readOnly={readOnly}
              />
              {expandedGroups.has(index) && (
                <div className="w-full">
                  {group.tools.map((tool) => {
                    const toolId = getToolIdentifier(tool);
                    const toolIndex = toolIndexMap.get(toolId) ?? -1;

                    return (
                      <ToolRow
                        key={tool.toolUrn}
                        groupName={group.title}
                        availableToolUrns={toolset?.tools
                          ?.map((t) => t.toolUrn)
                          .concat(selectionMode === "add" ? selectedUrns : [])
                          .filter((urn) => !selectedForRemoval.has(urn))}
                        tool={tool}
                        onUpdate={(updates) => onToolUpdate?.(tool, updates)}
                        isUpdating={isToolUpdating}
                        isSelected={
                          selectionMode === "add"
                            ? selectedSet.has(toolId)
                            : selectedForRemoval.has(toolId)
                        }
                        isFocused={toolIndex === focusedToolIndex}
                        onCheckboxChange={(checked) =>
                          handleCheckboxChange(toolId, checked)
                        }
                        onTestInPlayground={onTestInPlayground}
                        onRemove={
                          selectionMode !== "add" && onToolsRemove
                            ? () => onToolsRemove([toolId])
                            : undefined
                        }
                        onToolClick={onToolClick}
                        readOnly={readOnly}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasChanges && !selectionMode && (
        <div className="sticky right-0 bottom-0 left-0 mt-4 flex justify-center">
          <div className="border-neutral-softest bg-background flex items-center gap-4 border px-4 py-3 shadow-lg">
            <p className="text-foreground text-sm">
              {selectedForRemoval.size} tool(s) selected
            </p>
            <div className="flex items-center gap-2">
              <kbd className="border-neutral-softest bg-muted text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 border px-1.5 font-mono text-[10px] font-medium opacity-100 select-none">
                <span className="text-xs">⌘</span>K
              </kbd>
              <span className="text-muted-foreground text-sm">for actions</span>
            </div>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
